import { Injectable, inject } from '@angular/core';
import { Title, Meta } from '@angular/platform-browser';
import { DOCUMENT } from '@angular/common';
import { TranslateService } from '@ngx-translate/core';

export interface SEOData {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  url?: string;
  type?: string;
  siteName?: string;
  locale?: string;
  canonical?: string;
  noindex?: boolean;
  nofollow?: boolean;
  twitterCard?: 'summary' | 'summary_large_image' | 'app' | 'player';
  twitterSite?: string;
  twitterCreator?: string;
}

@Injectable({
  providedIn: 'root',
})
export class SeoService {
  private readonly title = inject(Title);
  private readonly meta = inject(Meta);
  private readonly document = inject(DOCUMENT) as Document;
  private readonly translate = inject(TranslateService);

  private readonly defaultSiteName = 'SIMVet Monitor';
  private readonly defaultImage = '/assets/logos/simvet-light.png';
  private readonly baseUrl = 'https://simvetmonitor.com';

  /**
   * Update SEO meta tags for the current page
   */
  updateSEO(data: SEOData): void {
    const currentLang = this.translate.getCurrentLang() || 'en';
    const locale = data.locale || this.getLocaleFromLang(currentLang);

    // Update title
    if (data.title) {
      const fullTitle = `${data.title} | ${this.defaultSiteName}`;
      this.title.setTitle(fullTitle);
      this.updateMetaTag('property', 'og:title', fullTitle);
      this.updateMetaTag('name', 'twitter:title', fullTitle);
    }

    // Update description
    if (data.description) {
      this.updateMetaTag('name', 'description', data.description);
      this.updateMetaTag('property', 'og:description', data.description);
      this.updateMetaTag('name', 'twitter:description', data.description);
    }

    // Update keywords
    if (data.keywords) {
      this.updateMetaTag('name', 'keywords', data.keywords);
    }

    // Update image
    const imageUrl = data.image || this.defaultImage;
    const fullImageUrl = imageUrl.startsWith('http') ? imageUrl : `${this.baseUrl}${imageUrl}`;
    this.updateMetaTag('property', 'og:image', fullImageUrl);
    this.updateMetaTag('name', 'twitter:image', fullImageUrl);

    // Update URL
    const url = data.url || this.document.location.href;
    this.updateMetaTag('property', 'og:url', url);

    // Update type
    const type = data.type || 'website';
    this.updateMetaTag('property', 'og:type', type);

    // Update site name
    const siteName = data.siteName || this.defaultSiteName;
    this.updateMetaTag('property', 'og:site_name', siteName);

    // Update locale
    this.updateMetaTag('property', 'og:locale', locale);

    // Update canonical URL
    if (data.canonical) {
      this.updateCanonicalUrl(data.canonical);
    } else {
      this.updateCanonicalUrl(this.document.location.pathname);
    }

    // Update robots
    const robots = this.buildRobotsMeta(data.noindex, data.nofollow);
    this.updateMetaTag('name', 'robots', robots);

    // Update Twitter Card
    const twitterCard = data.twitterCard || 'summary_large_image';
    this.updateMetaTag('name', 'twitter:card', twitterCard);

    if (data.twitterSite) {
      this.updateMetaTag('name', 'twitter:site', data.twitterSite);
    }

    if (data.twitterCreator) {
      this.updateMetaTag('name', 'twitter:creator', data.twitterCreator);
    }
  }

  /**
   * Update hreflang tags for multilingual support
   */
  updateHreflangTags(alternateUrls: { lang: string; url: string }[]): void {
    // Remove existing hreflang links
    const existingLinks = this.document.querySelectorAll('link[rel="alternate"][hreflang]');
    existingLinks.forEach((link: Element) => link.remove());

    // Add new hreflang links
    const head = this.document.head;
    alternateUrls.forEach(({ lang, url }) => {
      const link = this.document.createElement('link');
      link.setAttribute('rel', 'alternate');
      link.setAttribute('hreflang', lang);
      link.setAttribute('href', url);
      head.appendChild(link);
    });

    // Add x-default
    if (alternateUrls.length > 0) {
      const defaultLink = this.document.createElement('link');
      defaultLink.setAttribute('rel', 'alternate');
      defaultLink.setAttribute('hreflang', 'x-default');
      defaultLink.setAttribute('href', alternateUrls[0].url);
      head.appendChild(defaultLink);
    }
  }

  /**
   * Remove hreflang tags
   */
  removeHreflangTags(): void {
    const existingLinks = this.document.querySelectorAll('link[rel="alternate"][hreflang]');
    existingLinks.forEach(link => link.remove());
  }

  /**
   * Update canonical URL
   */
  private updateCanonicalUrl(url: string): void {
    const fullUrl = url.startsWith('http') ? url : `${this.baseUrl}${url}`;

    // Remove existing canonical link
    const existingLink = this.document.querySelector('link[rel="canonical"]');
    let canonicalLink: HTMLLinkElement;

    if (existingLink) {
      canonicalLink = existingLink as HTMLLinkElement;
    } else {
      // Create new canonical link
      canonicalLink = this.document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      this.document.head.appendChild(canonicalLink);
    }

    canonicalLink.setAttribute('href', fullUrl);
  }

  /**
   * Update or create a meta tag
   */
  private updateMetaTag(attr: 'name' | 'property', key: string, value: string): void {
    const selector = `${attr}="${key}"`;
    const existingTag = this.meta.getTag(selector);

    if (existingTag) {
      this.meta.updateTag({ [attr]: key, content: value });
    } else {
      this.meta.addTag({ [attr]: key, content: value });
    }
  }

  /**
   * Build robots meta content
   */
  private buildRobotsMeta(noindex?: boolean, nofollow?: boolean): string {
    const parts: string[] = [];

    if (noindex) {
      parts.push('noindex');
    } else {
      parts.push('index');
    }

    if (nofollow) {
      parts.push('nofollow');
    } else {
      parts.push('follow');
    }

    return parts.join(', ');
  }

  /**
   * Get locale from language code
   */
  private getLocaleFromLang(lang: string): string {
    const localeMap: { [key: string]: string } = {
      en: 'en_US',
      es: 'es_ES',
      pt: 'pt_BR',
      fr: 'fr_FR',
      de: 'de_DE',
      it: 'it_IT',
    };

    return localeMap[lang] || 'en_US';
  }

  /**
   * Reset to default SEO values
   */
  reset(): void {
    this.title.setTitle(this.defaultSiteName);
    this.updateSEO({
      title: this.defaultSiteName,
      description: 'Enhance your teaching experience with our cutting-edge educational application. SIMVet allows instructors to vividly demonstrate and simulate clinical scenarios in animal patients, making vital signs monitoring engaging and effective.',
      image: this.defaultImage,
    });
  }
}

